import rp from "@reporting/rp-app-host";
import { utils } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { ContextData, MultiContextData, ReportContext } from "@reporting/rp-types/analytics";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { UidMultiContextDataItem, cloneContext, isMultiContext } from "../common";
import { ReportStore } from "../model/reportStore";
import { createName, getContextBroker } from "./context";

async function addLinkOrCopy(reportContext: ReportContext, module: ModuleCode, targetContext?: ReportContext) {
	const newContext = !targetContext;
	const cmd = await rp.host.openMenu({
		value: newContext ? createName(module) : '',
		mode: 'input',
		text: newContext
			? msg(`Add to new ${module == 'D' ? 'dashboard' : 'spreadsheet'}`)
			: msg(`Add to "{name}"`, {data: {name: targetContext.name}}),
		content: [
			{id: 'copy', name: msg(`add as copy`)},
			{id: 'link', name: msg(`add as link`)},
		]
	});

	if (cmd?.id == null) return null;

	targetContext = targetContext
		? await rp.ms('analytics').get<ReportContext>(`context/${targetContext.id_context}`)
		: {name: cmd.value || createName(module), module: module};

	const contextData = (targetContext.context || {}) as MultiContextData;
	contextData.items = contextData.items || [];

	contextData.items.push(cmd.id == 'copy'
		? {context: reportContext.context as ContextData}
		: {id_context: reportContext.id_context}
	);

	targetContext.context = contextData;

	if (newContext) {
		return await rp.ms('analytics').post<ReportContext>('context', targetContext);
	} else {
		return await rp.ms('analytics').put<ReportContext>('context', targetContext);
	}
}

/**
 * Add an item of the given type into an open dashboard. Item types
 * correspond to the "+" toolbar menu (VisualizationOptions.addMenu).
 * The item is appended through the dashboard store; returns the new
 * item or null when cancelled.
 */
export async function addItem(type: string, store: ReportStore) {
	if (!store) return null;
	switch (type) {
		case 'savedView': return addSavedView(store);
		case 'text': return addTextItem(store);
		case 'links': return addLinksItem(store);
		case 'sheet': return addSheetItem(store);
	}
	return null;
}

/** Add a free-form sheet (not connected to any report) into an open spreadsheet. */
async function addSheetItem(store: ReportStore) {
	const count = ((store.contextData as MultiContextData)?.items?.length ?? 0) + 1;
	const uid = store.addItem({
		title: `${msg('Sheet')} ${count}`,
		sheet_model: {},
	});
	return store.getItem(uid);
}

/**
 * Add a saved view into an open dashboard ("from the dashboard side").
 * The user picks a saved view and chooses whether to add it as a link
 * (referenced by id_context) or embed a copy of its context data.
 */
async function addSavedView(store: ReportStore) {
	const contexts = await rp.ms('analytics').get<ReportContext[]>('namedContext', {
		fields: ['id_context', 'name', 'module'],
	});

	const views = contexts?.filter(c =>
		!isMultiContext(c.module) && c.id_context != store.idContext
	) ?? [];

	const cmd = await rp.host.openMenu({
		text: msg('Select view'),
		content: views.map(v => ({id: v.id_context.toString(), name: v.name}))
	});
	if (cmd?.id == null) return null;

	const view = views.find(v => v.id_context == parseInt(cmd.id));
	if (!view) return null;

	const mode = await rp.host.openMenu({
		text: msg(`Add "{name}"`, {data: {name: view.name}}),
		content: [
			{id: 'link', name: msg(`add as link`)},
			{id: 'copy', name: msg(`add as copy`)},
		]
	});
	if (mode?.id == null) return null;

	let item: UidMultiContextDataItem;
	if (mode.id == 'copy') {
		const source = await getContextBroker().getContext({id_context: view.id_context});
		item = {title: view.name, context: cloneContext(source.context) as ContextData};
	} else {
		item = {title: view.name, id_context: view.id_context};
	}
	const uid = store.addItem(item);
	return store.getItem(uid);
}

/**
 * Add a plain text item (not connected to the data model) into an open dashboard.
 * The text content and formatting live in the item context view_options and are
 * edited via the dashboard editor.
 */
async function addTextItem(store: ReportStore) {
	const uid = store.addItem({
		context: {
			view: 'text',
			view_options: {content: msg('Text')},
		} as ContextData,
		style: {header: false},
	});
	return store.getItem(uid);
}

/**
 * Add a list of links to other saved views (not connected to the data model)
 * into an open dashboard. The links live in the item context view_options
 * and are edited via the dashboard editor.
 */
async function addLinksItem(store: ReportStore) {
	const uid = store.addItem({
		title: msg('Links'),
		context: {
			view: 'links',
			view_options: {items: []},
		} as ContextData,
	});
	return store.getItem(uid);
}

export async function add(context: ReportContext, module: ModuleCode) {
	const items = await rp.ms('analytics').get<ReportContext[]>('namedContext', {
		fields: ['id_context', 'name'],
		where: {module: module},
	});

	const name = module == 'D' ? 'dashboard' : 'spreadsheet';
	const cmd = await rp.host.openMenu({
		text: msg(`Select target ${name}`),
		value: '',
		mode: 'input',  // TODO: change to search when fixed
		content: [{
			id: '0',
			name: `<${msg(`new ${name}`)}>`
		}, ...items?.map(item => ({
			id: item.id_context.toString(),
			name: item.name
		}))]
	});

	if (cmd?.id == null) return null;

	const idTarget = parseInt(cmd.id);
	if (idTarget >= 0) {
		return addLinkOrCopy(context, module, items.find(c => c.id_context == idTarget));
	}

	return null;
}
